import React, { useCallback, useEffect, useState } from "react";
import { Auction } from "../../../models/auction";
import { ReMixChart } from "../../../components/recharts/ReMixCharts";
import {
  FormatAuctionDaily,
  FormatAuctionDailyComparison,
  FormatAuctionMonthly,
  FormatAuctionMonthlyComparison,
} from "./FormatData";
import { COLOR_CODES, formatDMY } from "../../../common";
import { format } from "date-fns";
import { getRaMetrics } from "../../../Rest_api/RestApi";
import { SearchBox } from "../../../components/Search";
import { ChartAreaLoader } from "../../../components/Loading";
import { BrushStart } from "../../../models/chart_model";
import { ExchangeColors } from "../Exchange3/FormatData";
import LegendSummaryTable, {
  ChartTable,
} from "../../../components/table/SummaryTable";
import { useLegendFilter } from "../../../hooks/useLegendFilter";
import Tooltip from "../../../components/tooltip";
import FilterMenu from "../../../components/FilterMenu";
import NewFilterContainer from "../../../components/NewFilterContainer ";
import MainPageContainer from "../../../components/MainPageContainer";
import SecondaryHeader from "../../Rajasthan/Demand/SecondaryHeader";
import { useSelector } from "react-redux";
import MetricChip from "../../../components/Metrics";

interface RAProps {
  forecastData: Auction[];
  startDate: Date;
  endDate: Date;
  loading: boolean;
}

export const RA: React.FC<RAProps> = ({
  forecastData,
  startDate,
  endDate,
  loading,
}) => {
  const dataTab = useSelector((state: any) => state.viewMode.activeView);
  const [isByMonthlyChart, setIsByMonthlyChart] = useState<boolean>(false);
  const [isComparative, setIsComparative] = useState<boolean>(true);
  const [selectedEntity, setSelectedEntity] = useState<string[]>([]);
  const [selectExc, setSelectExc] = useState<string[]>([]);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [selectedAuctioNumber, setSelectedAuctioNumber] = useState<string[]>(
    [],
  );
  const [selectedAuctionData, setSelectedAuctionData] = useState<string[]>([]);
  const [selectedRaVariables, setSelectedRaVariables] = useState<string[]>([]);
  const [filterLoading, setFilterLoading] = useState<boolean>(true);
  const [hasInitialized, setHasInitialized] = useState<boolean>(false);
  const [processedChartData, setProcessedChartData] = useState<any[]>([]);
  const [processedTableData, setProcessedTableData] = useState<any[]>([]);
  const [filteredAuctionsList, setFilteredAuctionsList] = useState<Auction[]>(
    [],
  );
  const EXCHANGE_COLOR_MAP: { [key: string]: string } = {
    DEEP: ExchangeColors[1],
    IEX: COLOR_CODES.iex,
    HPX: COLOR_CODES.hpx,
    PXIL: COLOR_CODES.pixl,
  };

  const handleSelectExc = (items: string[]) => {
    setSelectExc(items);
    setCurrentPage(1);
  };

  const getRaVariableOptions = (): string[] => {
    if (isComparative) {
      return ["Allocated", "Price", "Unallocated"];
    }
    return ["Allocated", "Unallocated", "Wt. Avg Price"];
  };

  const filterRaLegendsByVariable = (legends: any[]): any[] => {
    if (selectedRaVariables.length === 0) return legends;
    return legends.filter((legend) => {
      const dk = (legend.dataKey as string).toLowerCase();
      if (isComparative) {
        if (
          selectedRaVariables.includes("Allocated") &&
          dk.includes("total_mu") &&
          !dk.includes("unallocated")
        )
          return true;
        if (
          selectedRaVariables.includes("Unallocated") &&
          dk.includes("unallocated")
        )
          return true;
        if (selectedRaVariables.includes("Price") && legend.yAxisId === "right")
          return true;
        return false;
      } else {
        if (selectedRaVariables.includes("Allocated") && dk === "total_mu")
          return true;
        if (
          selectedRaVariables.includes("Unallocated") &&
          dk === "unallocated_mu"
        )
          return true;
        if (
          selectedRaVariables.includes("Wt. Avg Price") &&
          legend.yAxisId === "right"
        )
          return true;
        return false;
      }
    });
  };

  const filteredAuctions = useCallback(
    (
      filterByEntity = true,
      filterByAuctionNumber = true,
      filterByAuctionDate = true,
    ): Auction[] => {
      let res = forecastData;
      if (selectExc.length > 0) {
        res = res.filter((auction) =>
          selectExc.includes(auction.exchange_type__name!),
        );
      }
      if (selectedEntity.length > 0 && filterByEntity) {
        res = res.filter((auction) =>
          selectedEntity.includes(auction.buyer__name!),
        );
      }
      if (selectedAuctioNumber.length > 0 && filterByAuctionNumber) {
        res = res.filter((auction) =>
          selectedAuctioNumber.includes(auction.auction_no!),
        );
      }
      if (selectedAuctionData.length > 0 && filterByAuctionDate) {
        res = res.filter((auction) =>
          selectedAuctionData.includes(auction.auction_date!),
        );
      }
      return res;
    },
    [
      forecastData,
      selectExc,
      selectedEntity,
      selectedAuctioNumber,
      selectedAuctionData,
    ],
  );

  const getActiveExc = () => {
    return selectExc.length > 0 ? selectExc : EXCHANGE_TYPES;
  };

  // const paginatedAuctions = filteredAuctions().slice(
  //   (currentPage - 1) * ITEMS_PER_PAGE,
  //   currentPage * ITEMS_PER_PAGE
  // );

  const legendsForTable: any[] = isByMonthlyChart
    ? [
        ...getActiveExc().map((type, index) => ({
          dataKey: `total_mu_${type.toLowerCase()}`,
          name: `${type}`,
          // stroke: ExchangeColors[index + 1],
        })),
        ...getActiveExc().map((type, index) => ({
          dataKey: `weighted_avg_rs_per_kwh_${type.toLowerCase()}`,
          name: `${type} Price`,
          stroke: EXCHANGE_COLOR_MAP[type],
          // yAxisId: "right",
        })),
      ]
    : [
        {
          dataKey: "buy_total_quantity_mw",
          name: "Buy Quantity(MW)",
          // stroke: COLOR_CODES.secondary,
        },
        {
          dataKey: "allocated_quantity_mw",
          name: "Allocated Quantity(MW)",
          // stroke: COLOR_CODES.primary,
        },
        {
          dataKey: "accepted_price_kwh",
          name: "Allocated Price(kWh)",
          // stroke: COLOR_CODES.secondary,
          yAxisId: "right",
        },
      ];

  // ▶ Legend filter hook — syncs chart legend clicks with table columns
  const {
    visibleLegends,
    handleLegendSelect,
    selectedLegends: filteredSelectedLegends,
  } = useLegendFilter(legendsForTable);

  const tablePar = {
    legends: visibleLegends,
    data: processedTableData,
    tableData: processedTableData,
    itemsPerPage: 100,
    unit: isByMonthlyChart ? "MUs" : "MW",
    extraKeys: isByMonthlyChart
      ? ["date"]
      : [
          "auction_no",
          "auction_date",
          "delivery_start_date",
          "delivery_start_time",
          "delivery_end_date",
          "delivery_end_time",
          "buyer__name",
          "exchange_type__name",
        ],
    extraLabels: isByMonthlyChart
      ? ["Date"]
      : [
          "Auction No",
          "Auction Date",
          "Start Date",
          "Start Time",
          "End Date",
          "End Time",
          "Buyer",
          "Exchange",
        ],
    filename: "Reverse Auction Data",
    showTotal: false,
    tableHeight: "calc(100vh - 315px)",
  };

  const aa = filteredAuctions(true, true, false)
    .map((auction) => ({
      label: formatDMY(auction.auction_date!),
      value: auction.auction_date!,
    }))
    .filter((v, i, a) => a.findIndex((t) => t.value === v.value) === i)
    .sort((a, b) => new Date(b.value).getTime() - new Date(a.value).getTime());

  const [raMetrics, setRaMetrics] = useState({
    totalBuyQty: 0,
    totalAllocatedQty: 0,
    avgPrice: 0,
    totalAuctions: 0,
  });

  useEffect(() => {
    const fetchMetrics = async () => {
      try {
        const res = await getRaMetrics({
          start_date: format(startDate, "yyyy-MM-dd"),
          end_date: format(endDate, "yyyy-MM-dd"),
          interval: isByMonthlyChart ? "Monthly" : "Daily",
          exchange: selectExc.length > 0 ? selectExc : ["all"],
          type: "Cumulative",
          variable: selectedRaVariables,
        });

        if (res) {
          setRaMetrics({
            totalBuyQty: res.total_buy_quantity_mw || 0,
            totalAllocatedQty: res.total_allocated_quantity_mw || 0,
            avgPrice: res.average_allocated_price_kwh || 0,
            totalAuctions: res.no_of_auctions || 0,
          });
        }
      } catch (error) {
        console.error("Error fetching RA metrics:", error);
      }
    };

    if (startDate && endDate) {
      fetchMetrics();
    }
  }, [
    startDate,
    endDate,
    selectExc,
    isByMonthlyChart,
    isComparative,
    selectedRaVariables,
  ]);

  useEffect(() => {
    setFilterLoading(true);
    const timeoutId = setTimeout(() => {
      const activeAuctions = filteredAuctions();
      setFilteredAuctionsList(activeAuctions);

      // Prepare Table Data
      if (isByMonthlyChart) {
        const td = FormatAuctionDailyComparison({
          auctions: activeAuctions,
          start_date: startDate.toString(),
          end_date: endDate.toString(),
        }) as any;
        setProcessedTableData(td);
      } else {
        setProcessedTableData(activeAuctions);
      }

      // Prepare Chart Data
      if (!isComparative) {
        if (isByMonthlyChart) {
          setProcessedChartData(
            FormatAuctionMonthly({
              start_date: formatDateString(startDate),
              end_date: formatDateString(endDate),
              auctions: activeAuctions,
            }),
          );
        } else {
          setProcessedChartData(
            FormatAuctionDaily({
              auctions: activeAuctions,
              start_date: startDate.toString(),
              end_date: endDate.toString(),
            }) as any,
          );
        }
      } else {
        if (isByMonthlyChart) {
          setProcessedChartData(
            FormatAuctionMonthlyComparison({
              start_date: formatDateString(startDate),
              end_date: formatDateString(endDate),
              auctions: activeAuctions,
            }),
          );
        } else {
          setProcessedChartData(
            (FormatAuctionDailyComparison({
              auctions: activeAuctions,
              start_date: startDate.toString(),
              end_date: endDate.toString(),
            }) as any) ?? [],
          );
        }
      }

      setFilterLoading(false);
      setHasInitialized(true);
    }, 0);

    return () => clearTimeout(timeoutId);
  }, [
    forecastData,
    startDate,
    endDate,
    selectExc,
    selectedEntity,
    selectedAuctioNumber,
    selectedAuctionData,
    isByMonthlyChart,
    isComparative,
    filteredAuctions,
  ]);

  const cumulativeLegends: any[] = [
    {
      dataKey: "total_mu",
      name: "Allocated MU",
      type: 1,
      stackId: "a",
      legendColor: "rgb(159, 159, 159)",
    },
    {
      dataKey: "unallocated_mu",
      name: "Unallocated MU",
      type: 1,
      stackId: "a",
      legendColor: "gray",
    },
    {
      dataKey: "weighted_avg_rs_per_kwh",
      name: "Wt. Avg Price(Rs/KWh)",
      yAxisId: "right",
    },
  ];

  const comparativeLegends: any[] = [
    ...getActiveExc().flatMap((type) => [
      {
        dataKey: `total_mu_${type.toLowerCase()}`,
        name: `${type} Allocated`,
        stackId: type,
        type: 1,
      },
      {
        dataKey: `unallocated_mu_${type.toLowerCase()}`,
        name: `${type} Unallocated`,
        stackId: type,
        type: 1,
      },
    ]),
    ...getActiveExc().map((type) => ({
      dataKey: `weighted_avg_rs_per_kwh_${type.toLowerCase()}`,
      name: `${type} Price`,
      yAxisId: "right",
    })),
  ];

  return (
    <div className="gas-page-container">
      <NewFilterContainer>
        <div className="side-filters-container text-nowrap">
          <FilterMenu
            title="Interval"
            items={["Daily", "Monthly"]}
            selectedItems={[isByMonthlyChart ? "Monthly" : "Daily"]}
            onSelect={(_, index) => setIsByMonthlyChart(index === 1)}
          />
          <FilterMenu
            title="Exchange"
            items={EXCHANGE_TYPES}
            selectedItems={getActiveExc()}
            setSelectedItems={handleSelectExc}
          />

          <FilterMenu
            title="Type"
            items={["Cumulative", "Comparative"]}
            selectedItems={[isComparative ? "Comparative" : "Cumulative"]}
            onSelect={(_, index) => {
              setIsComparative(index === 1);
              setSelectedRaVariables([]);
            }}
          />
          <FilterMenu
            title="Variable"
            items={getRaVariableOptions()}
            selectedItems={
              selectedRaVariables.length > 0
                ? selectedRaVariables
                : getRaVariableOptions()
            }
            setSelectedItems={(items) => {
              setSelectedRaVariables(
                items.length === getRaVariableOptions().length ? [] : items,
              );
            }}
          />
        </div>
      </NewFilterContainer>
      <MainPageContainer>
        <SecondaryHeader showViewMode />

        <div className="flex flex-wrap gap-4 my-0 justify-center">
          {[
            {
              label: "Total Buy Quantity",
              value: raMetrics.totalBuyQty,
              unit: "MW",
            },
            {
              label: "Total Allocated Quantity",
              value: raMetrics.totalAllocatedQty,
              unit: "MW",
            },
            {
              label: "Wt. Avg. Allocated Price",
              value: raMetrics.avgPrice,
              unit: "Rs/KWh",
            },
            {
              label: "Number of Auctions",
              value: raMetrics.totalAuctions,
              unit: "",
            },
          ].map((item) => (
            <MetricChip key={item.label} icon={null} items={[item]} />
          ))}
        </div>

        <div className="gas-chart-container" style={{ minHeight: 0, flex: 1 }}>
          <div className="gas-heading gap-x-10 w-full">
            <h2 className="chartHeading w-60 whitespace-nowrap">
              Reverse Auction
            </h2>
            <div className="flex gap-4 justify-end min-h-[38px] items-center flex-1 w-full">
              <div className="search-box flex-1 min-w-[200px] max-w-[280px]">
                <SearchBox
                  options={filteredAuctions(false, false, false)
                    .map((auction) => ({
                      label: auction.buyer__name!,
                      value: auction.buyer__name!,
                    }))
                    .filter(
                      (v, i, a) =>
                        a.findIndex((t) => t.value === v.value) === i,
                    )}
                  isMany={true}
                  selectedOptions={selectedEntity.map((entity) => ({
                    label: entity,
                    value: entity,
                  }))}
                  onChange={(e: any) => {
                    setSelectedEntity(e.map((item: any) => item.value));
                    setCurrentPage(1);
                  }}
                  placeholder="Select Entity"
                />
              </div>
              <div className="search-box flex-1 min-w-[200px] max-w-[280px]">
                <SearchBox
                  options={filteredAuctions(false, false, false)
                    .map((auction) => ({
                      label: formatDMY(auction.auction_date!),
                      value: auction.auction_date!,
                    }))
                    .filter(
                      (v, i, a) =>
                        a.findIndex((t) => t.value === v.value) === i,
                    )
                    .sort(
                      (a, b) =>
                        new Date(b.value).getTime() -
                        new Date(a.value).getTime(),
                    )}
                  isMany={true}
                  selectedOptions={selectedAuctionData.map((auctionData) => ({
                    label: auctionData,
                    value: auctionData,
                  }))}
                  onChange={(e: any) =>
                    setSelectedAuctionData(e.map((item: any) => item.value))
                  }
                  placeholder="Auction Date"
                />
              </div>
              <div className="auctionnum-search-box flex-1 min-w-[200px] max-w-[280px]">
                <SearchBox
                  options={filteredAuctions(false, false, false)
                    .map((auction) => ({
                      label: auction.auction_no!,
                      value: auction.auction_no!,
                    }))
                    .filter(
                      (v, i, a) =>
                        a.findIndex((t) => t.value === v.value) === i,
                    )}
                  isMany={true}
                  selectedOptions={selectedAuctioNumber.map(
                    (auctionNumber) => ({
                      label: auctionNumber,
                      value: auctionNumber,
                    }),
                  )}
                  onChange={(e: any) => {
                    setSelectedAuctioNumber(e.map((item: any) => item.value));
                    setCurrentPage(1);
                  }}
                  placeholder="Select Auction No."
                />
              </div>
            </div>
          </div>

          {loading || filterLoading || !hasInitialized ? (
            <ChartAreaLoader />
          ) : (
            <>
              {dataTab?.label === "Chart" && (
                <div className="raChart container-radius">
                  <ReMixChart
                    onlyTitle={true}
                    data={processedChartData}
                    xDataKey="date"
                    legends={filterRaLegendsByVariable(
                      isComparative ? comparativeLegends : cumulativeLegends,
                    )}
                    xaxisHeight={50}
                    unit="MUs"
                    secondYAxisLabel="Weighted Average Price (Rs/KWh)"
                    yAxisLabel="Total MUs"
                    chartContainerHeight="calc(100vh - 200px)"
                    showBrush={true}
                    brushStart={BrushStart.Start}
                    xAxisInterval="auto"
                  />
                </div>
              )}

              {dataTab?.label === "Summary" && (
                <LegendSummaryTable {...(tablePar as any)} />
              )}
              {dataTab?.label === "Table" && (
                <ChartTable {...(tablePar as any)} />
              )}
            </>
          )}
        </div>

        <div className="gna-footer-container">
          <div className="grid grid-cols-3 justify-between gap-5 ">
            <Tooltip
              note={<p>Reverse Auction</p>}
              text="1. IEX: Any Day(s) Single Side Reverse Auction. 2. HPX | Hindustan Power Exchange. 3. PXIL | Power Exchange of India Limited. 4. DEEP"
            >
              <p className="gna-footer-text">Source</p>
            </Tooltip>
            <p className="gna-footer-text col-span-1 text-center">
              Powered by <strong>GNA Energy</strong>
            </p>
            <strong className="gna-footer-text col-span-1 text-end">
              Beta Version
            </strong>
          </div>
        </div>
      </MainPageContainer>
    </div>
  );
};

const EXCHANGE_TYPES = ["IEX", "HPX", "PXIL", "DEEP"];

const formatDateString = (date: Date): string =>
  date.toLocaleDateString("en-GB").split("/").reverse().join("-");
